import { Location, SortDirectionEnum } from '@/models/gen/graphql';
import React, { useCallback } from 'react';
import useLocationsTableState, { LocationSortColumnEnum } from './hook';

import Input from '@/components/Input';
import Table from '@/common/Table';

export type LocationsTableHeaderProps = {
  data: Location[];
  selected: string[];
  onSelect: (ids: string | string[]) => void;
};

const toTableDirection = (dir: SortDirectionEnum | undefined): 'asc' | 'desc' | undefined => {
  if (dir === SortDirectionEnum.Asc) return 'asc';
  if (dir === SortDirectionEnum.Desc) return 'desc';
  return undefined;
};

/**
 * Header row for the locations table.
 * Provides a select-all checkbox and sortable column labels.
 * Sorting state is managed via Zustand and drives a BE re-query in the parent.
 */
const LocationsTableHeader = ({ data, selected, onSelect }: LocationsTableHeaderProps): React.JSX.Element => {
  const [{ sortBy, sortDirection }, onSort] = useLocationsTableState(({ state }) => [
    { sortBy: state.sorting.column, sortDirection: state.sorting.direction },
    state.onSort,
  ]);

  const sorting = useCallback(
    (key: LocationSortColumnEnum) => ({
      direction: toTableDirection(sortBy === key ? sortDirection : undefined),
      onSort: (): void => onSort(key),
    }),
    [sortBy, sortDirection, onSort]
  );

  const isSelectAll = data.length > 0 && selected.length === data.length;
  const handleSelectAll = useCallback((): void => {
    onSelect(data.map((row): string => row.id));
  }, [data, onSelect]);

  return (
    <Table.Row header>
      <Table.Cell className="SELECT">
        <Input type="checkbox" name="SELECT_ALL_LOCATIONS" checked={isSelectAll} onChange={handleSelectAll} />
      </Table.Cell>
      <Table.Cell className="NAME alternate" sorting={sorting(LocationSortColumnEnum.name)}>
        Location
      </Table.Cell>
      <Table.Cell className="CITY_NAME" sorting={sorting(LocationSortColumnEnum.cityName)}>
        City
      </Table.Cell>
      <Table.Cell className="STATE_CODE" sorting={sorting(LocationSortColumnEnum.stateCode)}>
        State
      </Table.Cell>
      <Table.Cell className="ZIP_CODE" sorting={sorting(LocationSortColumnEnum.zipCode)}>
        Zip Code
      </Table.Cell>
      <Table.Cell className="PHONE_NUMBER alternate" sorting={sorting(LocationSortColumnEnum.phoneNumber)}>
        Phone
      </Table.Cell>
    </Table.Row>
  );
};

export default React.memo(LocationsTableHeader);
